import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import './MissionGridCalendar.css';
import { useI18n } from '../i18n/I18nContext';

const statusColor = (status) => {
  switch (status) {
    case 'EN_COURS': return '#10B981';
    case 'EN_ATTENTE': return '#F59E0B';
    case 'TERMINEE': return '#3B82F6';
    case 'ANNULEE': return '#EF4444';
    default: return '#6B7280';
  }
};

const statusClass = (status) => {
  if (!status) return 'unknown';
  return status.toLowerCase();
};

function MissionGridCalendar({ missions = [], drivers = [], onSelectMission }) {
  const { t } = useI18n();
  const [anchorDate, setAnchorDate] = useState(dayjs().format('YYYY-MM-DD'));

  const days = useMemo(
    () => [0, 1, 2].map((d) => dayjs(anchorDate).startOf('day').add(d, 'day')),
    [anchorDate]
  );

  const getDriverLabel = (driver) => {
    if (!driver) return null;
    const first = driver.nom || driver.firstname || '';
    const last = driver.prenom || driver.lastname || '';
    const full = `${first} ${last}`.trim();
    if (full) return full;
    if (driver.email) return driver.email;
    if (driver.id) return `${t('gantt.grid.driver')} #${driver.id}`;
    return t('gantt.grid.driver');
  };

  const getVehicleLabel = (vehicle) => {
    if (!vehicle) return null;
    if (vehicle.immatriculation) return vehicle.immatriculation;
    if (vehicle.registration) return vehicle.registration;
    if (vehicle.id) return `${t('gantt.grid.vehicle')} #${vehicle.id}`;
    return t('gantt.grid.vehicle');
  };

  const resources = useMemo(() => {
    const driverResources = drivers.map((driver) => {
      const driverId = driver?.id ?? driver?.chauffeurId ?? driver?.driverId ?? null;
      const label = getDriverLabel(driver) || (driverId ? `${t('gantt.grid.driver')} #${driverId}` : t('gantt.grid.driver'));
      const key = driverId != null ? `driver-${driverId}` : `driver-${label.toLowerCase()}`;
      return { key, label, driverId, missions: [] };
    });

    missions.forEach((m) => {
      const driverId = m.affectationMission?.chauffeur?.id ?? m.affectationMission?.chauffeurId ?? m.affectationMission?.driverId ?? m.assignedChauffeurId ?? null;
      if (driverId != null) {
        const found = driverResources.find((d) => d.driverId === driverId);
        if (found) { found.missions.push(m); return; }
      }
      const unassigned = driverResources.find((d) => d.key === 'unassigned');
      if (unassigned) unassigned.missions.push(m);
    });

    const hasUnassigned = driverResources.some((d) => d.key === 'unassigned');
    const resourcesWithUnassigned = hasUnassigned ? driverResources : [...driverResources, { key: 'unassigned', label: t('common.unassigned'), missions: [] }];

    missions.forEach((m) => {
      const driverId = m.affectationMission?.chauffeur?.id ?? m.affectationMission?.chauffeurId ?? m.affectationMission?.driverId ?? m.assignedChauffeurId ?? null;
      if (driverId == null) {
        const unassigned = resourcesWithUnassigned.find((d) => d.key === 'unassigned');
        if (unassigned) unassigned.missions.push(m);
      } else {
        const found = resourcesWithUnassigned.find((d) => d.driverId === driverId);
        if (!found) {
          const unassigned = resourcesWithUnassigned.find((d) => d.key === 'unassigned');
          if (unassigned) unassigned.missions.push(m);
        }
      }
    });

    const assigned = resourcesWithUnassigned.filter(r => r.key !== 'unassigned');
    assigned.sort((a, b) => (b.missions.length || 0) - (a.missions.length || 0));
    const unassigned = resourcesWithUnassigned.find(r => r.key === 'unassigned');
    return unassigned ? [...assigned, unassigned] : assigned;
  }, [missions, drivers, t]);

  const windowStart = days[0].startOf('day');
  const windowEnd = days[2].endOf('day');

  const stackMissions = (missions) => {
    if (missions.length === 0) return { missions: [], laneHeights: [], minHeight: 0, maxLanes: 0 };
    const sorted = [...missions].sort((a, b) => a._startTime - b._startTime || a._endTime - b._endTime);
    const lanes = [];
    const laneHeights = [];

    sorted.forEach((mission) => {
      let placed = false;
      for (let i = 0; i < lanes.length; i++) {
        const lane = lanes[i];
        const last = lane[lane.length - 1];
        if (!(mission._startTime < last._endTime && mission._endTime > last._startTime)) {
          lane.push(mission);
          mission._laneIndex = i;
          laneHeights[i] = Math.max(laneHeights[i], mission._barHeight);
          placed = true;
          break;
        }
      }
      if (!placed) {
        const i = lanes.length;
        lanes[i] = [mission];
        mission._laneIndex = i;
        laneHeights[i] = mission._barHeight;
      }
    });

    const gap = 8;
    const baseTop = 6;
    const laneOffsets = laneHeights.map((_, idx) => {
      if (idx === 0) return baseTop;
      let offset = baseTop;
      for (let i = 0; i < idx; i++) offset += laneHeights[i] + gap;
      return offset;
    });

    sorted.forEach((m) => { m._laneTop = laneOffsets[m._laneIndex] || baseTop; });
    const minHeight = laneHeights.length >= 0 ? laneOffsets[laneHeights.length - 1] + laneHeights[laneHeights.length - 1] + baseTop : 0;
    return { missions: sorted, laneHeights, minHeight, maxLanes: laneHeights.length };
  };

  const grid = useMemo(() => {
    return resources.map((res) => {
      const missionsInWindow = res.missions.map((m) => {
        const start = dayjs(m.departureDate);
        const end = dayjs(m.arrivalDate);
        if (!start.isBefore(windowEnd) || !end.isAfter(windowStart)) return null;
        const clampedStart = start.isBefore(windowStart) ? windowStart : start;
        const clampedEnd = end.isAfter(windowEnd) ? windowEnd : end;
        const dayWidth = 100 / days.length;
        const startDayIndex = clampedStart.startOf('day').diff(windowStart, 'day');
        const endDayIndex = clampedEnd.startOf('day').diff(windowStart, 'day');
        const leftPct = Math.max(0, startDayIndex * dayWidth);
        const spanDays = Math.max(1, endDayIndex - startDayIndex + 1);
        const widthPct = Math.min(100 - leftPct, spanDays * dayWidth);
        const lines = 1 + 2 + 1 + (m.affectationMission?.chauffeur ? 1 : 0) + (m.affectationMission?.vehicule ? 1 : 0);
        const barHeight = Math.max(56, Math.min(130, 20 + lines * 18));
        return { ...m, _left: leftPct, _width: widthPct, _startTime: clampedStart.valueOf(), _endTime: clampedEnd.valueOf(), _barHeight: barHeight };
      }).filter(Boolean);
      const stacked = stackMissions(missionsInWindow);
      return { ...res, missionsInWindow: stacked.missions, maxLanes: stacked.maxLanes, minHeight: stacked.minHeight };
    });
  }, [resources, windowStart, windowEnd]);

  return (
    <div className="mgc">
      <div className="mgc__header">
        <div className="mgc__header-left">
          <h1 className="mgc__title">{t('gantt.grid.title')}</h1>
          <p className="mgc__subtitle">
            {days[0].format('MMM D')} - {days[2].format('MMM D, YYYY')}
          </p>
        </div>
        <div className="mgc__header-right">
          <button className="mgc__today-btn" onClick={() => setAnchorDate(dayjs().format('YYYY-MM-DD'))}>{t('gantt.grid.today')}</button>
          <div className="mgc__nav">
            <button className="mgc__nav-btn" onClick={() => setAnchorDate(dayjs(anchorDate).subtract(3, 'day').format('YYYY-MM-DD'))}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
            <button className="mgc__nav-btn" onClick={() => setAnchorDate(dayjs(anchorDate).add(3, 'day').format('YYYY-MM-DD'))}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
          </div>
        </div>
      </div>

      <div className="mgc__legend">
        {[
          { status: 'EN_ATTENTE', label: t('missions.status.pending') },
          { status: 'EN_COURS', label: t('missions.status.in_progress') },
          { status: 'TERMINEE', label: t('missions.status.completed') },
          { status: 'ANNULEE', label: t('missions.status.cancelled') }
        ].map(({ status, label }) => (
          <div key={status} className="mgc__legend-item">
            <span className="mgc__legend-dot" style={{ background: statusColor(status) }} />
            <span className="mgc__legend-label">{t(`missions.status.${status.toLowerCase()}`)}</span>
          </div>
        ))}
      </div>

      <div className="mgc__container">
        <div className="mgc__grid">
          <div className="mgc__grid-header mgc__grid-header--timeline">
            <div className="mgc__header-cell mgc__header-cell--resource">
              <span className="mgc__header-title">{t('gantt.grid.resources')}</span>
              <span className="mgc__resource-count">{resources.length}</span>
            </div>
            <div className="mgc__header-cell mgc__header-cell--timeline">
              <div className="mgc__timeline-grid" style={{ gridTemplateColumns: `repeat(${days.length}, 1fr)` }}>
                {days.map((day) => (
                  <div key={day.format('YYYY-MM-DD')} className="mgc__timeline-day">
                    <div className="mgc__timeline-day-label">{day.format('ddd')}</div>
                    <div className="mgc__timeline-day-date">{day.format('DD')}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mgc__grid-body">
            <div className="mgc__vertical-lines">
              {days.map((day) => <div key={`line-${day.format('YYYY-MM-DD')}`} className="mgc__vertical-line" />)}
              <div className="mgc__vertical-line mgc__vertical-line--last" />
            </div>

            {grid.length === 0 && (
              <div className="mgc__empty-state">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                <p className="mgc__empty-text">{t('gantt.grid.noMissions')}</p>
              </div>
            )}

            {grid.map((res) => (
              <div key={res.key} className="mgc__row mgc__row--timeline">
                <div className="mgc__cell mgc__cell--resource">
                  <div className="mgc__resource-info">
                    <div className="mgc__resource-avatar">{res.label.charAt(0).toUpperCase()}</div>
                    <div className="mgc__resource-details">
                      <div className="mgc__resource-name">{res.label}</div>
                      <div className="mgc__resource-missions">{t('gantt.grid.missionsCount', { count: res.missions.length })}</div>
                    </div>
                  </div>
                </div>
                <div className="mgc__cell mgc__cell--timeline">
                  <div className="mgc__timeline-bars" style={{ minHeight: `${res.missionsInWindow.length ? ((res.minHeight || 0) / 16) : 0}rem`, paddingTop: '0' }}>
                    {res.missionsInWindow.map((mission) => (
                      <div key={mission.id} className={`mgc__mission mgc__mission--${statusClass(mission.status)}`} style={{ left: `calc(${mission._left}% + 0.625rem)`, width: `calc(${mission._width}% - 1.25rem)`, top: `${(mission._laneTop || 6) / 16}rem`, height: `${(mission._barHeight || 56) / 16}rem` }} onClick={() => onSelectMission?.(mission.id)}>
                        <div className="mgc__mission-status" style={{ background: statusColor(mission.status) }} />
                        <div className="mgc__mission-content">
                          <div className="mgc__mission-route">
                            <span className="mgc__mission-location" title={mission.departureLocation}>{mission.departureLocation}</span>
                            <svg className="mgc__mission-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            <span className="mgc__mission-location" title={mission.arrivalLocation}>{mission.arrivalLocation}</span>
                          </div>
                          <div className="mgc__mission-time">
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" /><path d="M8 5V8L10 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
                            <div className="mgc__mission-times">
                              <div><span className="mgc__time-label">{t('gantt.grid.start')}</span> {dayjs(mission.departureDate).format('MMM D, HH:mm')}</div>
                              <div><span className="mgc__time-label">{t('gantt.grid.arrival')}</span> {dayjs(mission.arrivalDate).format('MMM D, HH:mm')}</div>
                            </div>
                          </div>
                          {(mission.affectationMission?.chauffeur || mission.affectationMission?.vehicule) && (
                            <div className="mgc__mission-resources">
                              {mission.affectationMission?.chauffeur && <span className="mgc__mission-chip"><span role="img" aria-label="driver">👤</span> {getDriverLabel(mission.affectationMission.chauffeur)}</span>}
                              {mission.affectationMission?.vehicule && <span className="mgc__mission-chip"><span role="img" aria-label="vehicle">🚚</span> {getVehicleLabel(mission.affectationMission.vehicule)}</span>}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MissionGridCalendar;